import type { InsightHandler } from "@brains/plugins";
import { toISODateString, getYesterday, getDaysAgo } from "@brains/utils/date";
import type { CloudflareClient } from "../lib/cloudflare-client";

/**
 * The two queries this insight runs. CloudflareClient does far more; asking for
 * all of it meant a test could not supply two readings without asserting it was
 * a whole client.
 */
export type TrafficStatsClient = Pick<
  CloudflareClient,
  "getWebsiteStats" | "getTopPages"
>;
import { getErrorMessage } from "@brains/utils/error";

const OVERVIEW_DAYS = 7;
const TOP_PAGES_LIMIT = 10;

/**
 * Create the traffic-overview insight handler.
 * Returns recent traffic summary from Cloudflare Web Analytics.
 * Gracefully degrades when client is unavailable or API fails.
 */
export function createTrafficOverviewInsight(
  client: TrafficStatsClient | undefined,
): InsightHandler {
  return async () => {
    if (!client) {
      return {
        unavailable: true,
        reason: "Cloudflare analytics not configured",
      };
    }

    const end = getYesterday();
    const start = getDaysAgo(OVERVIEW_DAYS);

    const startDate = toISODateString(start);
    const endDate = toISODateString(end);

    try {
      const [stats, topPages] = await Promise.all([
        client.getWebsiteStats({ startDate, endDate }),
        client.getTopPages({ startDate, endDate, limit: TOP_PAGES_LIMIT }),
      ]);

      return {
        days: OVERVIEW_DAYS,
        startDate,
        endDate,
        pageviews: stats.pageviews,
        visitors: stats.visitors,
        topPages,
      };
    } catch (error) {
      return {
        error: getErrorMessage(error, "Failed to fetch analytics"),
        days: OVERVIEW_DAYS,
      };
    }
  };
}
